import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SearchX } from 'lucide-react';
import {
  Alert,
  Button,
  EmptyState,
  KeyValueList,
  KeyValueRow,
  PageHeader,
  SectionHeader,
  StatusPill,
  Timeline,
  TimelineStep,
  WhatsAppContactCard,
} from '@/components/common';
import { useCustomerClaims } from '@/hooks/useCustomerClaims';
import type { ClaimCase } from '@/types/claim';
import { claimStatus, claimWhatsAppUrl, formatDateTime } from '@/utils/claimCases';
import { formatDay } from '@/utils/productCases';

const BACK = { to: '/customer/claims', label: 'Claims' };

/** Submitted → (photos) → Review → Resolution → Closed, from what the API tells us. */
function progressSteps(claim: ClaimCase): TimelineStep[] {
  const { stage, outcome } = claim;
  const closed = stage === 'closed';
  const steps: TimelineStep[] = [
    stage === 'draft'
      ? { title: 'Claim started', meta: 'Finish your claim to submit it', state: 'current' }
      : { title: 'Claim submitted', meta: formatDateTime(claim.filedAt), state: 'done' },
  ];
  if (claim.photos.length) {
    steps.push({ title: 'Photos added', meta: `${claim.photos.length} ${claim.photos.length === 1 ? 'photo' : 'photos'}`, state: 'done' });
  }
  steps.push(
    closed
      ? { title: 'Review', meta: formatDateTime(claim.reviewedAt) || 'Completed', state: 'done' }
      : stage === 'review'
        ? { title: 'Review', meta: 'Our team is checking your claim · usually 2 working days', state: 'current' }
        : stage === 'submitted'
          ? { title: 'Review', meta: 'We’ll start reviewing it shortly', state: 'current' }
          : { title: 'Review', state: 'pending' },
  );
  steps.push(
    closed && outcome === 'approved'
      ? { title: 'Resolution', meta: 'Approved · repair, replacement or store credit', state: 'done' }
      : closed
        ? { title: 'Decision', meta: outcome === 'rejected' ? 'Not approved' : 'Cancelled', state: 'done' }
        : { title: 'Resolution', meta: 'Repair, replacement or store credit', state: 'pending' },
  );
  steps.push(closed ? { title: 'Claim closed', meta: formatDateTime(claim.closedAt), state: 'done' } : { title: 'Claim closed', state: 'pending' });
  return steps;
}

const Panel: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="tw-flex tw-flex-col tw-gap-[14px] tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-p-5">
    <SectionHeader title={title} />
    {children}
  </section>
);

/** T3A Claim details: progress timeline, what happened, details and WhatsApp help. */
const ClaimDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { claims, loading, error, reload } = useCustomerClaims();
  const claim = claims.find((c) => c.id === id);

  if (loading || error || !claim) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-6">
        <PageHeader title="Claim" back={BACK} />
        {loading ? (
          <div aria-busy className="tw-h-64 tw-animate-pulse tw-rounded-2xl tw-bg-ds-surface" />
        ) : error ? (
          <div className="tw-flex tw-flex-col tw-gap-3">
            <Alert tone="danger" title="Something went wrong">
              {error}
            </Alert>
            <Button variant="secondary" className="tw-self-start" onClick={reload}>
              Try again
            </Button>
          </div>
        ) : (
          <EmptyState
            icon={SearchX}
            title="Claim not found"
            description="This claim isn’t on your account. It may have been withdrawn."
            action={<Button onClick={() => navigate(BACK.to)}>Back to claims</Button>}
          />
        )}
      </div>
    );
  }

  const { tone, label } = claimStatus(claim);
  const rows: KeyValueRow[] = [
    { label: 'Product', value: [claim.productTitle, claim.merchant].filter(Boolean).join(' · ') },
    claim.planName && { label: 'Plan', value: claim.planName },
    claim.policyNumber && { label: 'Policy ID', value: claim.policyNumber },
    { label: 'Claim ID', value: claim.label },
    claim.issue && { label: 'Issue', value: claim.issue },
    claim.filedAt && { label: 'Filed on', value: formatDay(claim.filedAt) },
  ].filter(Boolean) as KeyValueRow[];

  return (
    <div className="tw-flex tw-flex-col tw-gap-6">
      <PageHeader
        title={claim.productTitle}
        badge={<StatusPill tone={tone}>{label}</StatusPill>}
        subtitle={['Claim ' + claim.label, claim.planName].filter(Boolean).join(' · ')}
        back={BACK}
      />

      <div className="tw-grid tw-items-start tw-gap-4 lg:tw-grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] lg:tw-gap-6">
        <div className="tw-flex tw-flex-col tw-gap-4">
          <Panel title="Claim progress">
            <Timeline steps={progressSteps(claim)} />
          </Panel>
          {(claim.description || claim.photos.length > 0) && (
            <Panel title="What happened">
              {claim.description && <p className="tw-text-[14px] tw-leading-5 tw-text-ds-text-secondary">{claim.description}</p>}
              {claim.photos.length > 0 && (
                <ul className="tw-flex tw-flex-wrap tw-gap-3">
                  {claim.photos.map((src, i) => (
                    <li key={src}>
                      <a
                        href={src}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tw-block tw-h-20 tw-w-20 tw-overflow-hidden tw-rounded-xl tw-bg-ds-subtle focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ds-brand"
                      >
                        <img src={src} alt={`Claim photo ${i + 1}`} loading="lazy" className="tw-h-full tw-w-full tw-object-cover" />
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          )}
        </div>
        <div className="tw-flex tw-flex-col tw-gap-4">
          <Panel title="Claim details">
            <KeyValueList rows={rows} />
          </Panel>
          <WhatsAppContactCard
            title="Questions about this claim?"
            description={`Chat with us on WhatsApp and quote claim ID ${claim.label}.`}
            href={claimWhatsAppUrl(claim)}
          />
        </div>
      </div>
    </div>
  );
};

export default ClaimDetailsPage;
